/**
 * Operator listing-variant channel_price writes.
 * Authority: set_listing_variant_channel_price (NULL = use channel rule).
 * Does not write Product Master or call providers.
 */
import { canManageListings } from "@/lib/permissions";
import { parseMoneyInput } from "@/lib/money-entry";
import { sellingPriceError } from "@/lib/listing-pricing";
import {
  formatStoredMoney,
  resolveVariantEffectiveChannelPrice,
} from "@/lib/listing-channel-price-display";
import type { PriceStrategy } from "@/lib/channels";
import type { PriceStrategyConfig } from "@/lib/listing-pricing";

const VARIANT_UUID =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function isListingVariantUuid(id: string): boolean {
  return VARIANT_UUID.test(id);
}

export function listingChannelOverrideControlsVisible(
  role: string | null | undefined,
  variantId: string,
): boolean {
  return canManageListings(role) && isListingVariantUuid(variantId);
}

export function parseListingChannelOverrideInput(
  raw: string,
): { ok: true; price: number } | { ok: false; error: string } {
  const parsed = parseMoneyInput(raw);
  if (parsed == null) return { ok: false, error: "Enter a valid channel price." };
  const err = sellingPriceError(parsed);
  if (err) return { ok: false, error: err };
  return { ok: true, price: parsed };
}

export function listingVariantChannelPriceRpcArgs(opts: {
  orgId: string;
  listingId: string;
  variantId: string;
  price: number | null;
}): {
  p_org_id: string;
  p_listing_id: string;
  p_variant_id: string;
  p_price: number | null;
} {
  return {
    p_org_id: opts.orgId,
    p_listing_id: opts.listingId,
    p_variant_id: opts.variantId,
    p_price: opts.price,
  };
}

export function resultingEffectiveAfterOverrideChange(opts: {
  priceStrategy: PriceStrategy;
  pricingConfig?: PriceStrategyConfig;
  canonicalBase: number | null;
  channelPrice: number | null;
}): number | null {
  return resolveVariantEffectiveChannelPrice({
    priceStrategy: opts.priceStrategy,
    pricingConfig: opts.pricingConfig,
    productSellingPrice: opts.canonicalBase,
    variantSellingPrice: null,
    channelPrice: opts.channelPrice,
  }).effective;
}

export function confirmClearListingOverrideCopy(opts: {
  options: string;
  currentOverride: number;
  resultingEffective: number | null;
  channelName: string;
  currency?: string | null;
}): { title: string; body: string; confirmLabel: string } {
  const current = formatStoredMoney(opts.currentOverride, opts.currency);
  const next = formatStoredMoney(opts.resultingEffective, opts.currency);
  return {
    title: `Remove ${current} override?`,
    body: [
      `${opts.options} will use this listing's channel rule.`,
      `New channel price: ${next}.`,
      "",
      `The price will sync automatically to ${opts.channelName}.`,
    ].join("\n"),
    confirmLabel: "Use channel rule",
  };
}

export function confirmSaveListingOverrideCopy(opts: {
  options: string;
  currentEffective: number | null;
  newPrice: number;
  channelName: string;
  currency?: string | null;
}): { title: string; body: string; confirmLabel: string } {
  return {
    title: "Save listing override?",
    body: [
      opts.options,
      `Current channel price: ${formatStoredMoney(opts.currentEffective, opts.currency)}.`,
      `New listing override: ${formatStoredMoney(opts.newPrice, opts.currency)}.`,
      `Channel: ${opts.channelName}.`,
      "",
      `The price will sync automatically to ${opts.channelName}.`,
    ].join("\n"),
    confirmLabel: "Save override",
  };
}

export type ListingVariantPriceSnapshot = {
  listingId: string;
  productSellingPrice: number | null;
  variantSellingPrice: Record<string, number | null>;
  channelPrice: Record<string, number | null>;
};

/** Local isolation: one listing + one variant. Product Master amounts are not written. */
export function applyListingVariantChannelPriceLocally(
  listings: ListingVariantPriceSnapshot[],
  write: { listingId: string; variantId: string; channelPrice: number | null },
): ListingVariantPriceSnapshot[] {
  return listings.map((listing) => {
    if (listing.listingId !== write.listingId) return listing;
    if (!(write.variantId in listing.channelPrice)) return listing;
    return {
      ...listing,
      productSellingPrice: listing.productSellingPrice,
      variantSellingPrice: { ...listing.variantSellingPrice },
      channelPrice: { ...listing.channelPrice, [write.variantId]: write.channelPrice },
    };
  });
}

export function mergeChannelPriceOverlay<
  T extends {
    variant_id?: string | null;
    channel_price?: number | null;
    variant?: { id?: string } | null;
    variants?: { id?: string } | { id?: string }[] | null;
  },
>(rows: T[], overlay: Record<string, number | null>): T[] {
  return rows.map((row) => {
    const nestedRaw = row.variant ?? row.variants ?? null;
    const nested = Array.isArray(nestedRaw) ? nestedRaw[0] ?? null : nestedRaw;
    const id = String(nested?.id || row.variant_id || "");
    if (!id || !(id in overlay)) return row;
    return { ...row, channel_price: overlay[id] };
  });
}
